import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { makeStyles } from 'tss-react/mui';
import { Button, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import AutoCompleteSelect from "./autocomplete";
import { getPhoneBookEntry } from "../utils/common";
import { dialDigits } from "../utils/contacts";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  // Keeps the field, keypad and buttons on one column of the same width.
  column: {
    width: "100%",
    maxWidth: "248px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  // Same look as the keys: a soft grey pill on the white card, no outline.
  autoComplete: {
    position: "relative",
    marginBottom: theme.spacing(1),
    // Doubled selector: wins over the shared autocomplete's white background.
    "&& .MuiOutlinedInput-root": {
      borderRadius: "28px",
      paddingLeft: theme.spacing(1.5),
      fontSize: "1.15rem",
      letterSpacing: "0.04em",
      backgroundColor: theme.palette.action.hover,
      transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      "& fieldset": {
        border: "none",
      },
      "&.Mui-focused": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
      },
    },
    "& .MuiInputBase-input": {
      textAlign: "center",
      padding: "10px 0",
    },
  },
  // Keypad and action row share one 3-column grid so the buttons line up.
  keyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 72px)",
    justifyContent: "center",
    justifyItems: "center",
    alignItems: "center",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1.5),
  },
  dialpad: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  key: {
    width: "56px",
    height: "56px",
    minWidth: "56px",
    padding: 0,
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    transition: "background-color 0.15s ease, transform 0.1s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
    "&:active": {
      transform: "scale(0.94)",
      backgroundColor: theme.palette.action.focus,
    },
  },
  keyNumber: {
    fontSize: "1.35rem",
    lineHeight: 1.1,
    fontWeight: 500,
    color: theme.palette.text.softer,
  },
  keyLetters: {
    fontSize: "0.6rem",
    lineHeight: 1,
    minHeight: "0.65rem",
    letterSpacing: "0.08em",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  // Sits just outside the right edge of the number field.
  clearButton: {
    position: "absolute",
    right: "-40px",
    top: "50%",
    transform: "translateY(-50%)",
    color: theme.palette.text.secondary,
  },
  actions: {
    paddingTop: theme.spacing(0.5),
  },
  callButton: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.success,
    boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.24)",
    "&:hover": {
      background: theme.palette.primary.success,
      filter: "brightness(0.95)",
    },
    "& svg": {
      fontSize: "1.75rem",
    },
    "&.Mui-disabled": {
      color: theme.palette.primary.contrastText,
      background: theme.palette.primary.success,
      opacity: 0.45,
      boxShadow: "none",
    },
  },
  secondaryButton: {
    color: theme.palette.text.secondary,
  },
  selectDevice: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: theme.palette.text.secondary,
  },
  selectDeviceText: {
    fontSize: ".7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
}));

const CallDialer = (props) => {
    const {
      selectedIndex,
      setSelectedIndex,
      deviceSelection,
      setDeviceSelection,
      phonebook,
      voip,
      value,
      setValue,
      handleDialClick,
      options,
      dialerInputCb,
      dtmfDialer,
      viewValue,
      setViewValue,
      transferCall,
    } = props;
    const { classes, cx } = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    // Text currently shown in the number field (digits from the keypad or a
    // typed name); drives the clear button.
    const [inputText, setInputText] = useState(typeof viewValue === 'string' ? viewValue : '');

    // Follow external resets of the dialled number (e.g. after a call ends).
    useEffect(() => {
      if (viewValue === "" || viewValue === null || viewValue === undefined) {
        setInputText("");
      } else if (typeof viewValue === 'string') {
        setInputText(viewValue);
      }
    }, [viewValue]);
    // const [viewValue, setViewValue] = useState(value);
    const dtmfTone = window.dtmfTone
    
    const rows = [
      [
        { number: 1, letters: "" },
        { number: 2, letters: "ABC" },
        { number: 3, letters: "DEF" },
      ],
      [
        { number: 4, letters: "GHI" },
        { number: 5, letters: "JKL" },
        { number: 6, letters: "MNO" },
      ],
      [
        { number: 7, letters: "PQRS" },
        { number: 8, letters: "TUV" },
        { number: 9, letters: "WXYZ" },
      ],
      [
        { number: "*", letters: "" },
        { number: 0, letters: "+" },
        { number: "#", letters: "" },
      ],
    ];

    // Keep the dial value (what the call button uses) in step with the text.
    const setDialText = (next) => {
      setInputText(next);
      setViewValue(next);
      setValue(next === '' ? null : getPhoneBookEntry(phonebook, dialDigits(next)));
    };

    const pressKey = (key) => {
      if (dtmfDialer) {
        dtmfTone.play()
      }
      if (dialerInputCb)
        dialerInputCb(key)
      setDialText(inputText + String(key));
    };

    const handleClear = () => setDialText("");

    // Suggest contacts whose name or any number contains what was typed;
    // nothing is suggested until the user types.
    const filterContacts = (contacts, state) => {
      const q = String(state.inputValue || "").trim().toLowerCase();
      if (!q) return [];
      return contacts.filter((c) => {
        const name = `${c.firstName} ${c.lastName}`.toLowerCase();
        const numbers = [
          c.voicemail && c.voicemail.mailbox,
          c.preferredDeviceNumbers && c.preferredDeviceNumbers.office,
          c.preferredDeviceNumbers && c.preferredDeviceNumbers.mobile,
          c.preferredDeviceNumbers && c.preferredDeviceNumbers.custom,
        ].filter(Boolean).map(String);
        return name.includes(q) || numbers.some((n) => n.includes(q));
      });
    };

    const handleBackspace = () => {
      if (inputText.length > 0) {
        setDialText(inputText.slice(0, -1));
      }
    };

    const buildKey = (col) => (
      <Button
        key={String(col.number)}
        className={classes.key}
        disableElevation
        aria-label={`dial ${col.number}`}
        onClick={() => pressKey(col.number)}
      >
        <span className={classes.keyNumber}>{col.number}</span>
        <span className={classes.keyLetters}>{col.letters || "\u00a0"}</span>
      </Button>
    );

    const buildRows = rows.flat().map(buildKey);

    const handleClickListItem = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuItemClick = (event, index) => {
      setDeviceSelection(options[index]);
      setSelectedIndex(index);
      setAnchorEl(null);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const selectOptions = useMemo(() => phonebook.map((option) => {
      const firstLetter = option.firstName[0].toUpperCase();
      return {
        firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
        ...option
      };
    }), [phonebook]);

    return (
      <div className={classes.root}>
        <div className={classes.column}>
          {
            !dtmfDialer && <div className={classes.autoComplete}>
              <AutoCompleteSelect
                viewValue={viewValue}
                setValue={setValue}
                setViewValue={setViewValue}
                options={selectOptions}
                phonebook={phonebook}
                onTextFieldKeyPress={(e) => {
                  if (value && e.key === 'Enter') {
                    handleDialClick()
                  }
                }}
                placeholder=""
                inputPlaceholder="Number or contact"
                size="small"
                hidePopupIcon
                disableClearable
                filterOptions={filterContacts}
                inputValue={inputText}
                onInputValueChange={setInputText}
                disableGrouping
              />
              {inputText.length > 0 && (
                <Tooltip title="Clear">
                  <IconButton
                    aria-label="clear number"
                    className={classes.clearButton}
                    size="small"
                    onClick={handleClear}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          }
          <div className={cx(classes.keyGrid, classes.dialpad)}>{buildRows}</div>

          {
            !dtmfDialer && <div className={cx(classes.keyGrid, classes.actions)}>
              <div>
                { voip.active !== 'yes' &&
                  <>
                    <Tooltip title="Call from">
                      <IconButton
                        aria-label="selected device"
                        className={classes.selectDevice}
                        onClick={handleClickListItem}
                        size="large">
                        <ArrowDropDownCircleOutlinedIcon />
                        <span className={classes.selectDeviceText}>
                          {options[selectedIndex]}
                        </span>
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="lock-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                    >
                      {options.map((option, index) => (
                        <MenuItem
                          key={index}
                          selected={option === deviceSelection}
                          onClick={(event) => handleMenuItemClick(event, index)}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                }
              </div>
              <Tooltip title={transferCall ? "Transfer" : "Call"}>
                <span>
                  <IconButton
                    aria-label={transferCall ? "transfer" : "dial"}
                    className={classes.callButton}
                    onClick={handleDialClick}
                    disabled={!value}
                    size="large">
                    { transferCall ? <PhoneForwardedIcon /> : <PhoneIcon /> }
                  </IconButton>
                </span>
              </Tooltip>
              <div>
                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      aria-label="backspace"
                      className={classes.secondaryButton}
                      onClick={handleBackspace}
                      disabled={inputText.length === 0}
                      size="large">
                      <BackspaceOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          }
        </div>
      </div>
    );
}

const mapStateToProps = (state) => {
  return {
    phonebook: state.phonebook,
    session: state.session,
    voip: state.user.voip,
  }
}
export default connect(mapStateToProps, null)(CallDialer);